/**
 * EvidencePackInputSchema — request body shape for `POST /v1/evidence-pack/build`.
 * W7-slice-b2 / ADR-0005 / Lesson §17.
 *
 * The route turns the editor-side state (project name, audience, configHash,
 * cards, signal bundle, readiness, linkedSignals) into the canonical pack
 * JSON, computes its sha256, and (optionally) writes it to R2. The OUTPUT
 * envelope is `{ pack, sha256, r2Key, disclaimer }`; this schema validates
 * only the INPUT.
 *
 * Karpathy: only the fields the route handler reads are validated. Rich
 * client envelopes (snapshot diffs, audience slices) are W10 — not the
 * fast path here.
 */

import { z } from "zod";

import { ComplianceRequirementCardSchema } from "./card";

const SignalBundleMetaSchema = z.object({
  bundleId: z.string().min(1),
  fetchedAt: z.string().min(1),
  count: z.number().int().nonnegative(),
});

const LinkedSignalSchema = z.object({
  ruleId: z.string().min(1),
  signalIds: z.array(z.string().min(1)),
  cardTitle: z.string(),
});

export const EvidencePackInputSchema = z.object({
  projectName: z.string().min(1),
  audience: z.enum([
    "InternalComplianceLead",
    "ExternalReviewerAuditor",
    "ExternalAuthority",
  ]),
  configHash: z.string().nullable(),
  cards: z.array(ComplianceRequirementCardSchema),
  signalBundle: SignalBundleMetaSchema.nullable(),
  readiness: z.unknown().optional(),
  linkedSignals: z.array(LinkedSignalSchema).optional(),
});
export type EvidencePackInput = z.infer<typeof EvidencePackInputSchema>;
